import { useId, useState, type ReactElement, type ReactNode } from 'react'

import { motion } from 'motion/react'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'
import { Badge } from '../../atoms/badge/badge'
import { Button } from '../../atoms/button/button'
import { Icon } from '../../atoms/icon/icon'
import { Text } from '../../atoms/text/text'
import { TextLink } from '../../atoms/text-link/text-link'
import { Checkbox } from '../../molecules/checkbox/checkbox'
import { Input } from '../../molecules/input/input'
import {
  RadioGroup,
  RadioGroupItem
} from '../../molecules/radio-group/radio-group'

import { OmniTableIcon } from './omni-table-icon'
import { OmniTablePopover } from './omni-table-popover'
import { TableScrollArea } from './table-scroll-area'

export type OmniTableFilterValues = Readonly<Record<string, readonly string[]>>

/** A category's draft, for controls the page renders in place of the options. */
export interface OmniTableFilterDraft {
  value: readonly string[]
  onChange: (value: readonly string[]) => void
}

export interface OmniTableFilterCategory {
  id: string
  label: string
  icon: ReactNode
  options: readonly string[]
  /** Optional display names for stable option IDs. Selection keeps the IDs. */
  optionLabels?: Readonly<Record<string, string>>
  allOptionsLabel?: string
  /** Multiple selection is the default; single selection uses Acqua radios. */
  selectionMode?: 'multiple' | 'single'
  /** The selected radio when no filter is applied, also restored by Clear. */
  defaultValue?: string
  searchable?: boolean
  loading?: boolean
  error?: ReactNode
  /**
   * The page's own controls in place of the option list, such as a range.
   * The panel still keeps the draft: the controls read `value` and write
   * `onChange` with the strings the page parses on Apply; `[]` is cleared.
   */
  content?: (draft: OmniTableFilterDraft) => ReactNode
}

export interface OmniTableFiltersProps {
  trigger: ReactElement<React.ComponentPropsWithRef<'button'>>
  categories: readonly OmniTableFilterCategory[]
  initialCategoryId: string
  values: OmniTableFilterValues
  onApply: (values: OmniTableFilterValues) => void
  label?: string
  /** Called when an opened panel selects a category, for loading its options. */
  onCategoryChange?: (categoryId: string) => void
  /** Reports the panel opening and closing, however it closes. */
  onOpenChange?: (open: boolean) => void
}

export function OmniTableFilters({
  trigger,
  categories,
  initialCategoryId,
  values,
  onApply,
  label = 'Filters',
  onCategoryChange,
  onOpenChange
}: OmniTableFiltersProps) {
  const panelId = useId()
  const reducedMotion = useReducedMotion()
  const [open, setOpen] = useState(false)
  const [activeId, setActiveId] = useState(initialCategoryId)
  const [draft, setDraft] = useState<OmniTableFilterValues>(values)
  const [query, setQuery] = useState('')
  const category =
    categories.find(item => item.id === activeId) ?? categories[0]
  const hasSelection = categories.some(
    item => (draft[item.id]?.length ?? 0) > 0
  )
  const hasChanges = categories.some(item => {
    const applied = values[item.id] ?? []
    const next = draft[item.id] ?? []
    return (
      applied.length !== next.length ||
      applied.some(value => !next.includes(value))
    )
  })
  const appliedCategoryCount = categories.filter(
    item => (values[item.id]?.length ?? 0) > 0
  ).length
  const draftCategoryCount = categories.filter(
    item => (draft[item.id]?.length ?? 0) > 0
  ).length
  const clearCategory = () => {
    if (!category) return
    setDraft(current => ({ ...current, [category.id]: [] }))
    setQuery('')
    if (values[category.id]?.length) onApply({ ...values, [category.id]: [] })
  }
  const clearAll = () => {
    setDraft({})
    setQuery('')
    if (appliedCategoryCount > 0) onApply({})
  }
  const selected = category ? draft[category.id] ?? [] : []
  const allSelected = Boolean(
    category?.options.length &&
      category.options.every(option => selected.includes(option))
  )
  const visibleOptions =
    category?.options.filter(option =>
      (category.optionLabels?.[option] ?? option)
        .toLowerCase()
        .includes(query.trim().toLowerCase())
    ) ?? []

  const changeOpen = (nextOpen: boolean) => {
    if (nextOpen === open) return
    if (nextOpen) {
      setDraft(values)
      setActiveId(initialCategoryId)
      onCategoryChange?.(initialCategoryId)
      setQuery('')
    }
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  return (
    <OmniTablePopover
      trigger={trigger}
      open={open}
      onOpenChange={changeOpen}
      label={label}
      className='font-body border-surface-200 bg-surface shadow-dialog grid h-[285px] w-[672px] max-w-[calc(100vw-32px)] grid-cols-[minmax(0,256px)_minmax(0,1fr)] overflow-hidden rounded-lg border max-sm:grid-cols-[minmax(0,40%)_minmax(0,1fr)]'
    >
      <nav
        aria-label='Filter categories'
        className='border-surface-200 flex min-w-0 flex-col border-r bg-[#fafbfd] p-2'
      >
        <div className='flex flex-col gap-1'>
          {categories.map(item => {
            const active = category?.id === item.id
            const count = draft[item.id]?.length ?? 0
            return (
              <button
                key={item.id}
                type='button'
                aria-label={item.label}
                aria-current={active ? 'true' : undefined}
                aria-controls={panelId}
                aria-describedby={
                  count > 0 ? `${panelId}-${item.id}-count` : undefined
                }
                onClick={() => {
                  setActiveId(item.id)
                  onCategoryChange?.(item.id)
                  setQuery('')
                }}
                className={cn(
                  'focus-visible:ring-primary-200 flex min-h-[51px] items-center gap-2 rounded-lg p-3 text-left text-[16px]/[24px] outline-none transition-colors duration-150 focus-visible:ring-2 motion-reduce:transition-none max-sm:px-2',
                  active
                    ? 'text-surface-title-50 bg-[#f1f2f6] font-semibold'
                    : 'text-surface-body font-normal hover:bg-[#f1f2f6]'
                )}
              >
                <span
                  aria-hidden='true'
                  className='[&>*]:text-[length:inherit]/none flex size-[1lh] shrink-0 items-center text-[1lh]/none [&>*]:block [&>*]:size-full'
                >
                  {item.icon}
                </span>
                <Text
                  as='span'
                  variant='body-lg'
                  weight={active ? 'semibold' : 'regular'}
                  className={cn(
                    'min-w-0 flex-1 truncate text-[16px]/[24px]',
                    active ? 'text-surface-title-50' : 'text-surface-body'
                  )}
                >
                  {item.label}
                </Text>
                {count > 0 ? (
                  <Badge
                    id={`${panelId}-${item.id}-count`}
                    aria-label={`${count} selected`}
                    className='text-surface-title-50 bg-primary-50 text-body-md size-[27px] shrink-0 rounded-full border-0 p-0'
                  >
                    {count}
                  </Badge>
                ) : (
                  <Icon
                    name='keyboard_arrow_right'
                    aria-hidden
                    className='shrink-0'
                  />
                )}
              </button>
            )
          })}
        </div>
        {draftCategoryCount > 1 && (
          <TextLink
            href='#'
            role='button'
            variant='body-lg'
            className='mt-auto flex min-h-12 items-center px-3 text-[16px]/[24px]'
            onClick={event => {
              event.preventDefault()
              clearAll()
            }}
            onKeyDown={event => {
              if (event.key === ' ') {
                event.preventDefault()
                clearAll()
              }
            }}
          >
            Clear all filters
          </TextLink>
        )}
      </nav>
      <div className='flex min-h-0 min-w-0 flex-col'>
        {category && (
          <motion.section
            key={category.id}
            initial={reducedMotion ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: reducedMotion ? 0 : 0.16,
              ease: [0.32, 0.72, 0, 1]
            }}
            id={panelId}
            aria-label={`${category.label} options`}
            className='flex min-h-0 flex-1 flex-col gap-4 p-4'
          >
            {category.content ? (
              category.content({
                value: selected,
                onChange: value =>
                  setDraft(current => ({ ...current, [category.id]: value }))
              })
            ) : (
              <>
                {category.searchable && (
                  <Input
                    aria-label={`Search ${category.label.toLowerCase()}`}
                    placeholder='Search'
                    size='lg'
                    inputClassName='text-caption-lg'
                    className='[&_[data-slot=input-leading-icon]]:text-surface-title-50 h-[51px] min-w-0 shrink-0 rounded-lg'
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    leadingIcon={<OmniTableIcon name='search' size='md' />}
                  />
                )}
                <div className='min-h-0 min-w-0 flex-1 [&_[data-slot=omni-table-viewport]]:rounded-none [&_[data-slot=omni-table-viewport]]:border-0'>
                  <TableScrollArea
                    height={category.searchable ? 120 : 187}
                    label={`${category.label} options`}
                    contentKey={`${category.id}:${query}`}
                  >
                    {() => (
                      <div className='flex flex-col items-start gap-2'>
                        {!category.loading &&
                          !category.error &&
                          category.selectionMode === 'single' && (
                            <RadioGroup
                              aria-label={`${category.label} options`}
                              className='gap-2'
                              value={selected[0] ?? category.defaultValue ?? ''}
                              onValueChange={value =>
                                setDraft(current => ({
                                  ...current,
                                  [category.id]:
                                    value && value !== category.defaultValue
                                      ? [String(value)]
                                      : []
                                }))
                              }
                            >
                              {category.allOptionsLabel && (
                                <RadioGroupItem
                                  value=''
                                  label={category.allOptionsLabel}
                                />
                              )}
                              {visibleOptions.map(option => (
                                <RadioGroupItem
                                  key={`${category.id}:${option}`}
                                  value={option}
                                  label={
                                    category.optionLabels?.[option] ?? option
                                  }
                                />
                              ))}
                            </RadioGroup>
                          )}
                        {!category.loading &&
                          !category.error &&
                          category.selectionMode !== 'single' &&
                          category.allOptionsLabel && (
                            <Checkbox
                              size='md'
                              label={category.allOptionsLabel}
                              aria-label={category.allOptionsLabel}
                              checked={allSelected}
                              indeterminate={
                                !allSelected && selected.length > 0
                              }
                              disabled={category.options.length === 0}
                              onCheckedChange={() =>
                                setDraft(current => ({
                                  ...current,
                                  [category.id]: allSelected
                                    ? []
                                    : [...category.options]
                                }))
                              }
                            />
                          )}
                        {!category.loading &&
                          !category.error &&
                          category.selectionMode !== 'single' &&
                          visibleOptions.map(option => (
                            <Checkbox
                              key={`${category.id}:${option}`}
                              size='md'
                              label={category.optionLabels?.[option] ?? option}
                              aria-label={
                                category.optionLabels?.[option] ?? option
                              }
                              checked={selected.includes(option)}
                              onCheckedChange={checked =>
                                setDraft(current => ({
                                  ...current,
                                  [category.id]: checked
                                    ? [...(current[category.id] ?? []), option]
                                    : (current[category.id] ?? []).filter(
                                        value => value !== option
                                      )
                                }))
                              }
                            />
                          ))}
                        {category.error ? (
                          <div
                            role='alert'
                            className='text-surface-body text-body-md'
                          >
                            {category.error}
                          </div>
                        ) : category.loading ? (
                          <p
                            role='status'
                            className='text-surface-body text-body-md'
                          >
                            Loading…
                          </p>
                        ) : (
                          visibleOptions.length === 0 && (
                            <p
                              role='status'
                              className='text-surface-body text-body-md'
                            >
                              No results found
                            </p>
                          )
                        )}
                      </div>
                    )}
                  </TableScrollArea>
                </div>
              </>
            )}
          </motion.section>
        )}
        <footer className='border-surface-200 flex h-16 shrink-0 items-center justify-end gap-2 border-t px-4'>
          {(selected.length > 0 ||
            (category && (values[category.id]?.length ?? 0) > 0)) && (
            <Button
              variant='tertiary'
              size='md'
              className='min-w-0 px-4'
              disabled={
                !category || !(values[category.id]?.length || selected.length)
              }
              onClick={clearCategory}
            >
              Clear
            </Button>
          )}
          <Button
            variant='secondary'
            size='md'
            className='min-w-0 px-4 sm:w-[103px]'
            onClick={() => changeOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            size='md'
            className='min-w-0 px-4 sm:w-[110px]'
            disabled={!hasSelection && !hasChanges}
            onClick={() => {
              onApply(draft)
              changeOpen(false)
            }}
          >
            Apply
          </Button>
        </footer>
      </div>
    </OmniTablePopover>
  )
}
